/**
 * 新規登録の下書き画像 (ProductDraftImages) への操作 (純関数)。
 * 詳細ページは画像枠の操作ごとに案件行を UPDATE するが、下書きでは案件行が無いのでクライアントの配列を更新し、
 * 「登録する」で 1 回の POST にまとめる (docs/features/manual-product-brand-first-flow.md §6.7)。
 * 配列の形は案件行と同じにして buildManualSlots / buildManualReorderPayload をそのまま使う。
 */
import type { ProductDraftImages } from '../../../features/products/productDraft';
import type { ProductAdditionalImage } from '../../../features/products/types';
import type { ManualSlotRef } from '../manualProductSlots';
import { buildManualReorderPayload, type ReorderItem } from './imageSlotReorder';

export interface DraftImageSource {
  url: string;
  /** SVG のときだけ付く原本のパス */
  original_path?: string | null;
}

/** 枠に画像を入れる (差し替えも同じ)。名前は新しい画像なのでクリアする */
export function setDraftImage(images: ProductDraftImages, ref: ManualSlotRef, source: DraftImageSource): ProductDraftImages {
  if (ref.type === 'main') {
    return { ...images, main_image_url: source.url, main_image_name: null, main_image_original_path: source.original_path || null };
  }
  const next: ProductAdditionalImage[] = [...images.additional_images];
  while (next.length <= ref.index) next.push({ url: '' });
  next[ref.index] = source.original_path ? { url: source.url, original_path: source.original_path } : { url: source.url };
  return { ...images, additional_images: next };
}

/** 枠の画像を外す。追加画像は詳細ページと同じく要素を詰める (後ろの画像が前に動く) */
export function removeDraftImage(images: ProductDraftImages, ref: ManualSlotRef): ProductDraftImages {
  if (ref.type === 'main') {
    return { ...images, main_image_url: '', main_image_name: null, main_image_original_path: null };
  }
  const next = [...images.additional_images];
  next.splice(ref.index, 1);
  return { ...images, additional_images: next };
}

/** 画像の名前 (空文字 = クリア) */
export function renameDraftImage(images: ProductDraftImages, ref: ManualSlotRef, name: string): ProductDraftImages {
  const value = name.trim();
  if (ref.type === 'main') {
    return { ...images, main_image_name: value || null };
  }
  return {
    ...images,
    additional_images: images.additional_images.map((img, i) => {
      if (i !== ref.index) return img;
      const { name: _old, ...rest } = img;
      void _old;
      return value ? { ...rest, name: value } : rest;
    }),
  };
}

/** 並べ替えの結果 (main + 追加画像の枠) を下書きに戻す。枠より後ろの画像は保持する */
export function reorderDraftImages(images: ProductDraftImages, items: ReorderItem[]): ProductDraftImages {
  const payload = buildManualReorderPayload(items, images.additional_images.slice(items.length - 1));
  return {
    main_image_url: payload.main_image_url,
    main_image_name: payload.main_image_name,
    main_image_original_path: payload.main_image_original_path,
    additional_images: payload.additional_images,
  };
}

export function countDraftImages(images: ProductDraftImages): number {
  return (images.main_image_url.trim() ? 1 : 0) + images.additional_images.filter((img) => !!img.url?.trim()).length;
}
